import {
  EnvironmentProviders,
  inject,
  makeEnvironmentProviders,
  provideAppInitializer,
} from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';

/**
 * Custom domain SVG icons — the ADR-017 exceptions, for concepts that
 * Material Icons does not cover. Files live in `public/icons/` and use
 * `currentColor`, so they take their color from CSS (design tokens).
 *
 * Add an icon here ONLY when no suitable Material icon exists; otherwise
 * use a ligature directly in the template: `<mat-icon>dashboard</mat-icon>`.
 * Ligature replacements for previously registered icons:
 *   dashboard → `dashboard`        admin → `admin_panel_settings`
 *   reports → `assessment`         support → `help_center`
 *   menuDropdown → `expand_more`   menuDropdownArrow → `chevron_right`
 *   exportIcon / downloadIcon → `download`
 *   tickIcon → `check`             emailIcon → `mail`
 *   profileDefault → `account_circle`
 */
const APP_SVG_ICONS: Readonly<Record<string, string>> = {
  '3dView': 'icons/3d-view.svg',
  analytics: 'icons/analytics.svg',
  cuts: 'icons/cuts.svg',
  tracking: 'icons/tracking.svg',
  folderSearch: 'icons/folder-search.svg',
  excavator: 'icons/excavator.svg',
  settingsAccountBox: 'icons/settings-account-box.svg',
  speed: 'icons/speed.svg',
  stop: 'icons/stop.svg',
  table: 'icons/table.svg',
  timer: 'icons/timer.svg',
  truck: 'icons/truck.svg',
};

function registerAppIcons(): void {
  const matIconRegistry = inject(MatIconRegistry);
  const sanitizer = inject(DomSanitizer);

  for (const [name, path] of Object.entries(APP_SVG_ICONS)) {
    matIconRegistry.addSvgIcon(name, sanitizer.bypassSecurityTrustResourceUrl(path));
  }
}

/**
 * Registers the custom SVG icons used across the app.
 * Runs once during app bootstrap, before the root component is created.
 */
export function provideAppIcons(): EnvironmentProviders {
  return makeEnvironmentProviders([provideAppInitializer(registerAppIcons)]);
}
